import { Card } from "@/components/ui/card";
import { CreditCard, Clock, CheckCircle, AlertCircle, FileText, HelpCircle } from "lucide-react";

const RefundPolicy = () => {
  const refundTimeline = [
    { method: "Credit/Debit Card", time: "5-7 business days" },
    { method: "Bank Transfer", time: "7-10 business days" },
    { method: "Mobile Money", time: "3-5 business days" },
    { method: "Store Credit", time: "Instant" }
  ];

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex items-center gap-3 mb-8">
        <CreditCard className="h-8 w-8 text-primary" />
        <h1 className="text-3xl font-bold">Refund Policy</h1>
      </div>
      
      <div className="space-y-6">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Refund Policy Overview
          </h2>
          <p className="text-muted-foreground">
            At SabiTradeNG, we strive to ensure customer satisfaction. Our refund policy is designed to be fair and transparent, protecting both buyers and sellers while maintaining trust in our marketplace.
          </p>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">When You're Eligible for a Refund</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Item Not as Described</p>
                <p className="text-sm text-muted-foreground">The product received significantly differs from the listing description or images.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Damaged or Defective Items</p>
                <p className="text-sm text-muted-foreground">Products arrived damaged or are not functioning as intended.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Order Not Delivered</p>
                <p className="text-sm text-muted-foreground">Your order was not delivered within the promised timeframe and tracking shows no delivery.</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              <div>
                <p className="font-medium">Cancelled Orders</p>
                <p className="text-sm text-muted-foreground">Orders cancelled before shipping or by mutual agreement with the seller.</p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            Refund Processing Times
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2">Payment Method</th>
                  <th className="text-left py-2">Processing Time</th>
                </tr>
              </thead>
              <tbody>
                {refundTimeline.map((item, index) => (
                  <tr key={index} className="border-b">
                    <td className="py-3">{item.method}</td>
                    <td className="py-3">{item.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            * Processing times begin once the refund has been approved and initiated.
          </p>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Refund Process</h2>
          <div className="space-y-4">
            <div>
              <h3 className="font-medium mb-2">Step 1: Request Refund</h3>
              <p className="text-sm text-muted-foreground">
                Submit a refund request through your order page within 7 days of delivery. Provide clear reasons and any supporting evidence (photos, videos).
              </p>
            </div>
            <div>
              <h3 className="font-medium mb-2">Step 2: Review Process</h3>
              <p className="text-sm text-muted-foreground">
                Our team reviews your request within 24-48 hours. We may contact you or the seller for additional information.
              </p>
            </div>
            <div>
              <h3 className="font-medium mb-2">Step 3: Approval & Processing</h3>
              <p className="text-sm text-muted-foreground">
                Once approved, the refund is initiated. You'll receive a confirmation email with tracking details for your refund.
              </p>
            </div>
            <div>
              <h3 className="font-medium mb-2">Step 4: Refund Receipt</h3>
              <p className="text-sm text-muted-foreground">
                Funds will be credited to your original payment method within the timeframe specified above.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Partial Refunds</h2>
          <p className="text-muted-foreground mb-3">
            Partial refunds may be granted in certain situations:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Item has obvious signs of use but wasn't disclosed</li>
            <li>• Some items in a multi-item order are satisfactory</li>
            <li>• Minor defects that don't affect primary functionality</li>
            <li>• Late delivery where the item is still usable</li>
          </ul>
        </Card>

        <Card className="p-6 border-orange-200 bg-orange-50">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            Important Information
          </h2>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li>• Original shipping fees are non-refundable unless the error was on our part</li>
            <li>• Gift card purchases cannot be refunded</li>
            <li>• Refunds for promotional items may be adjusted based on the promotion terms</li>
            <li>• Repeated refund requests may be subject to additional review</li>
            <li>• Sellers have 48 hours to respond to refund requests before automatic approval</li>
          </ul>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <HelpCircle className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Need Help?</h2>
          </div>
          <p className="text-muted-foreground mb-4">
            If you have questions about a refund or need assistance with the process, our customer support team is here to help.
          </p>
          <div className="flex gap-4">
            <a href="/contact" className="text-primary hover:underline">Contact Support</a>
            <a href="/help-center" className="text-primary hover:underline">Visit Help Center</a>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RefundPolicy;